import { Loader2, X } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// -----------------------------------------------------------------------------
// Button — enters a locked loading state immediately (double-click protection)
// -----------------------------------------------------------------------------
type Variant = 'default' | 'primary' | 'danger' | 'ghost';
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  iconOnly?: boolean;
  block?: boolean;
  loading?: boolean;
  /** Async handlers lock the button until they settle. */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', icon, iconOnly, block, loading, className = '', children, onClick, disabled, type = 'button', ...rest },
  ref,
) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const handle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick || lock.current) return;
    const r = onClick(e);
    if (r && typeof (r as Promise<unknown>).then === 'function') {
      lock.current = true;
      setBusy(true);
      try {
        await r;
      } catch {
        /* errors are surfaced by the caller (toast/inline) */
      } finally {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const isLoading = loading || busy;
  const cls = ['btn', variant !== 'default' ? variant : '', size !== 'md' ? size : '', iconOnly ? 'icon' : '', block ? 'block' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} type={type} className={cls} onClick={handle} disabled={disabled || isLoading} aria-busy={isLoading || undefined} {...rest}>
      {isLoading ? <Loader2 size={16} className="spin" aria-hidden /> : icon}
      {!iconOnly && children}
    </button>
  );
});

// -----------------------------------------------------------------------------
// Toggle — explicit ON/OFF text in addition to colour (accessibility)
// -----------------------------------------------------------------------------
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  const autoId = useId();
  const descId = `${id ?? autoId}-desc`;
  return (
    <button
      type="button"
      role="switch"
      id={id}
      className="toggle"
      aria-checked={checked}
      aria-describedby={description ? descId : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-track" aria-hidden>
        <span className="toggle-state">{checked ? 'ON' : 'OFF'}</span>
      </span>
      <span>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span className="sr-only">{checked ? ' (on)' : ' (off)'}</span>
        {description && (
          <span id={descId} className="hint" style={{ display: 'block' }}>
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

// -----------------------------------------------------------------------------
// Segmented control
// -----------------------------------------------------------------------------
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode; title?: string }>;
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} title={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modal — focus trap, focus restore, ESC, scroll lock, nested-safe, dirty guard
// -----------------------------------------------------------------------------
const openModals: string[] = [];

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size,
  dirty,
  initialFocus,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'narrow' | 'wide';
  /** When true, closing asks for confirmation so typed data is not lost by accident. */
  dirty?: boolean;
  initialFocus?: string;
  labelledBy?: string;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const requestClose = useCallback(() => {
    if (dirtyRef.current && !window.confirm('Discard your unsaved changes?')) return;
    onClose();
  }, [onClose]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = ref.current;
    const active = document.activeElement as HTMLElement | null;
    // Remember the opener — but never an element inside this modal (effects may re-run).
    if (active && !el?.contains(active)) restore.current = active;
    openModals.push(id);
    document.body.classList.add('no-scroll');
    if (!el?.contains(document.activeElement)) {
      const target = (initialFocus && el?.querySelector<HTMLElement>(initialFocus)) || el?.querySelector<HTMLElement>('[data-autofocus]') || el?.querySelector<HTMLElement>('input, textarea, select, button');
      target?.focus();
    }
    return () => {
      openModals.splice(openModals.indexOf(id), 1);
      if (!openModals.length) document.body.classList.remove('no-scroll');
      // Return focus to whatever opened the modal — only if it really closed (not a re-run).
      setTimeout(() => {
        if (openModals.includes(id)) return;
        const r = restore.current;
        if (r?.isConnected) r.focus();
      }, 0);
    };
  }, [open, id, initialFocus]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== id) return; // only the top-most modal reacts
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
      }
      if (e.key === 'Tab' && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
          (x) => x.offsetParent !== null,
        );
        if (!f.length) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, id, requestClose]);

  if (!open) return null;
  const titleId = labelledBy ?? `${id}-title`;
  return createPortal(
    <div
      className="modal-root"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div ref={ref} className={`modal ${size ?? ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <Button variant="ghost" iconOnly icon={<X size={18} />} aria-label="Close" onClick={requestClose} />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  danger,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown> | void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="narrow"
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} data-autofocus onClick={() => onConfirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <p>{message}</p>}
      {children}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Menu — dropdown with keyboard navigation
// -----------------------------------------------------------------------------
export interface MenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void | Promise<unknown>;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
}

export function Menu({
  trigger,
  items,
  align = 'right',
  label,
}: {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu'; 'aria-controls': string }) => ReactNode;
  /** `custom` renders inline controls (e.g. a row of buttons) inside the menu. */
  items: Array<MenuItem | 'sep' | { label: string; header: true } | { custom: ReactNode }>;
  align?: 'left' | 'right';
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const id = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // The menu is portalled to <body> with fixed positioning, so no scrolling list, card or
  // overflow:hidden row can clip it. It opens below the trigger, or above when there is no room.
  const place = useCallback(() => {
    const a = anchor.current?.getBoundingClientRect();
    const m = menu.current;
    if (!a || !m) return;
    const gap = 6;
    const pad = 8;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const left = Math.max(pad, Math.min(align === 'left' ? a.left : a.right - m.offsetWidth, vw - m.offsetWidth - pad));
    let top = a.bottom + gap;
    if (top + m.offsetHeight > vh - pad) top = a.top - gap - m.offsetHeight >= pad ? a.top - gap - m.offsetHeight : Math.max(pad, vh - pad - m.offsetHeight);
    setPos({ top, left });
  }, [align]);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place, items.length]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => place();
    const outside = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (!anchor.current?.contains(t) && !menu.current?.contains(t)) setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
    };
  }, [open, place]);

  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLElement>('.menu-item:not([disabled])')?.focus({ preventScroll: true });
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) anchor.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    // Keys pressed inside the menu belong to the menu (not to rows or global shortcuts).
    e.stopPropagation();
    const els = [...(menu.current?.querySelectorAll<HTMLElement>('.menu-item:not([disabled])') ?? [])];
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(i + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      els[e.key === 'Home' ? 0 : els.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      close(true);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      close(true);
    }
  };
  return (
    <div className="menu-anchor" ref={anchor}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu', 'aria-controls': id })}
      {open &&
        createPortal(
        <div
          ref={menu}
          className="menu"
          role="menu"
          id={id}
          aria-label={label}
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, opacity: 0 }}
          onKeyDown={onKey}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it, i) =>
            it === 'sep' ? (
              <div key={i} className="menu-sep" role="separator" />
            ) : 'custom' in it ? (
              <div key={i} className="menu-custom" role="none">
                {it.custom}
              </div>
            ) : 'header' in it ? (
              <div key={i} className="menu-label">
                {it.label}
              </div>
            ) : (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`menu-item ${it.danger ? 'danger' : ''}`}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  void it.onSelect();
                }}
              >
                {it.icon}
                <span>{it.label}</span>
                {it.hint && <span className="right">{it.hint}</span>}
              </button>
            ),
          )}
        </div>,
          document.body,
        )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// States
// -----------------------------------------------------------------------------
export function Empty({ icon = '✨', title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="big" aria-hidden>
        {icon}
      </div>
      <strong>{title}</strong>
      {children && <div className="small">{children}</div>}
      {action && <div className="mt">{action}</div>}
    </div>
  );
}

export function ErrorState({ title = "We couldn't load this.", error, onRetry }: { title?: string; error?: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : null;
  return (
    <div className="error-state" role="alert">
      <div className="big" aria-hidden>
        ⚠️
      </div>
      <strong>{title}</strong>
      {msg && <div className="small">{msg}</div>}
      {onRetry && (
        <div className="mt">
          <Button onClick={onRetry}>Retry</Button>
        </div>
      )}
    </div>
  );
}

export function Skeleton({ rows = 3, height = 56 }: { rows?: number; height?: number }) {
  return (
    <div className="stack sm" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-row muted" role="status">
      <Loader2 size={16} className="spin" aria-hidden />
      <span>{label}…</span>
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <div className={`field ${className ?? ''}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? (
        <span className="error-text" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="hint">{hint}</span>
      )}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}
